import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { apiRequest } from '../../utils';
import './ProfileCompleteness.css';

/* How far along the viewer's own profile is — a ring beside the member home's bell, a bar on
   /profile — and one tap onto the screen where the next open item is answered.

   ★ The percentage is the SERVER's. What "done" means lives in the store next to the field spec
   it counts (``mutations/person_profile.profile_completeness``), so the ring and the bar can
   never disagree, and a field added to the profile editor later starts counting here on its own
   rather than after somebody remembers to widen a list in a component.

   ★ It routes on the open item's SECTION, not always to /edit-profile. Everything but the socials
   is answered on the profile form; sending somebody who is one link short to a form with no link
   field on it would be a nudge that leads nowhere.

   ★ The ring renders NOTHING once there is nothing left to fill in. A meter pinned at 100% beside
   the bell is decoration that costs a corner of every screen it sits on forever. The card keeps
   its finished state, because there it is the answer to a question the person asked by opening
   their own profile. */

/* Where each open item is answered. The store names the section; the routes are the frontend's
   business and stay here. */
const SECTION_ROUTE = {
  basics: '/edit-profile',
  about: '/edit-profile',
  connections: '/connections',
};

const RING_SIZE = 38;
const RING_STROKE = 3.5;
const RADIUS = (RING_SIZE - RING_STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const Ring = ({ percent }) => (
  <svg className="pc-ring" width={RING_SIZE} height={RING_SIZE} aria-hidden="true">
    <circle className="pc-ring__track" cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RADIUS}
            strokeWidth={RING_STROKE} fill="none" />
    <circle className="pc-ring__arc" cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RADIUS}
            strokeWidth={RING_STROKE} fill="none"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - Math.max(0, Math.min(100, percent)) / 100)} />
    {/* the "%" earns its width: a bare number next to a notification bell reads as a count */}
    <text className="pc-ring__pct" x="50%" y="50%" dominantBaseline="central" textAnchor="middle">
      {percent}%
    </text>
  </svg>
);

const ProfileCompleteness = ({ variant = 'ring', className = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    try {
      setState(await apiRequest('GET', '/profile/completeness'));
    } catch {
      /* silent — a nudge that cannot be fetched is simply not drawn */
    }
  }, []);

  /* Re-read when the tab comes back: the profile is edited on another screen of this same app,
     and coming back to a home still saying 60% after filling three fields in reads as broken. */
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  if (!state) return null;

  const percent = Number(state.percent) || 0;
  const done = !!state.is_complete;
  const next = state.next;
  const nextSection = (state.missing && state.missing[0] && state.missing[0].section) || 'basics';
  const nextLabel = next ? t(`pcItem_${next}`) : null;
  const go = () => navigate(SECTION_ROUTE[nextSection] || '/edit-profile');

  /* A PILL — the same nudge as the ring, in a row of actions rather than beside the bell, and
     saying in words what the ring says in a number. It exists because the ring sits on the top
     line while the thing it asks for is done two taps away: repeating the percentage next to an
     action somebody is already reaching for is what makes it a prompt instead of a badge.

     ★ Hidden at 100%, exactly like the ring above — there is nothing left to ask for, the top
     line has stopped showing a number, and a pill repeating a number that is no longer anywhere
     would be repeating nothing. The caller supplies the class so it matches whatever row it
     lands in; only the percentage keeps a look of its own. */
  if (variant === 'pill') {
    if (done) return null;
    return (
      <button type="button" className={className || 'pc-pill'} onClick={go}
              title={nextLabel || t('pcTitle')}
              aria-label={t('pcAria', { percent, next: nextLabel })}>
        <Icon name="user-pen" />
        <span>{t('pcUpdateProfile')}</span>
        <span className="pc-pill__pct">{percent}%</span>
      </button>
    );
  }

  if (variant === 'ring') {
    if (done) return null;
    return (
      <button type="button" className={`pc-ring-btn ${className}`} onClick={go}
              title={nextLabel || t('pcTitle')}
              aria-label={t('pcAria', { percent, next: nextLabel })}>
        <Ring percent={percent} />
      </button>
    );
  }

  return (
    <button type="button" className={`pc-card ${done ? 'pc-card--done' : ''} ${className}`}
            onClick={go}>
      <div className={`pc-card__icon ${done ? 'pc-card__icon--done' : ''}`}>
        <Icon name={done ? 'check' : 'user-pen'} />
      </div>
      <div className="pc-card__text">
        <div className="pc-card__head">
          <span className="pc-card__title">{t('pcTitle')}</span>
          <span className="pc-card__pct">{t('pcPercentDone', { percent })}</span>
        </div>
        <div className="pc-bar" role="progressbar" aria-valuenow={percent}
             aria-valuemin="0" aria-valuemax="100">
          <div className="pc-bar__fill" style={{ width: `${percent}%` }} />
        </div>
        <span className="pc-card__next">{done ? t('pcDone') : nextLabel}</span>
      </div>
      <Icon name="angle-right" className="pc-card__chevron" />
    </button>
  );
};

export default ProfileCompleteness;
